import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type {
	CellData,
	ColumnFiltersState,
	OnChangeFn,
	RowData,
	SortingState,
	TableFeatures,
	Cell as TanStackCell,
	Column as TanStackColumn,
	ColumnDef as TanStackColumnDef,
	Header as TanStackHeader,
	Row as TanStackRow,
	Table as TanStackTable,
} from "@tanstack/react-table";
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createSortedRowModel,
	filterFns,
	flexRender,
	sortFns,
	stockFeatures,
	tableFeatures,
	createColumnHelper as tanStackCreateColumnHelper,
	useTable,
} from "@tanstack/react-table";

import { buttonVariants } from "@workspace/ui/components/button";
import { DebouncedInput } from "@workspace/ui/components/debounced-input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@workspace/ui/components/select";
import {
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	Table as TableRoot,
	TableRow,
} from "@workspace/ui/components/table";

/* eslint-disable @typescript-eslint/no-unused-vars -- declaration merging: the type
   parameters must repeat react-table's own, used or not. */
declare module "@tanstack/react-table" {
	interface TableMeta<
		in out TFeatures extends TableFeatures,
		in out TData extends RowData,
	> {
		handleAction?: (
			rowIndex: number,
			action: string,
			params?: never | null,
		) => void;
	}

	interface ColumnMeta<
		in out TFeatures extends TableFeatures,
		in out TData extends RowData,
		TValue extends CellData = CellData,
	> {
		filterVariant?: "text" | "range" | "select";
	}
}
/* eslint-enable @typescript-eslint/no-unused-vars */

/**
 * The feature set every DataTable uses (HA-740, react-table 9).
 *
 * v8 bundled every feature, so every table/column/row method existed; `stockFeatures` keeps
 * that. v8 also resolved every built-in filter/sort fn by name ("auto", "includesString",
 * "alphanumeric", …); registering the full `filterFns` / `sortFns` registries keeps that too.
 * The row models are the five this component passed to v8 (filtered, sorted, faceted ×3).
 */
export const dataTableFeatures = tableFeatures({
	...stockFeatures,
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(), // client-side faceting
	facetedUniqueValues: createFacetedUniqueValues(), // unique values for select filter/autocomplete
	facetedMinMaxValues: createFacetedMinMaxValues(), // min/max values for range filter
	filterFns,
	sortFns,
});

export type DataTableFeatures = typeof dataTableFeatures;

// v8-shaped aliases with the feature set applied, so column files keep `ColumnDef<Row>` etc.
export type ColumnDef<
	TData extends RowData,
	TValue extends CellData = CellData,
> = TanStackColumnDef<DataTableFeatures, TData, TValue>;
export type Column<
	TData extends RowData,
	TValue extends CellData = CellData,
> = TanStackColumn<DataTableFeatures, TData, TValue>;
export type Row<TData extends RowData> = TanStackRow<DataTableFeatures, TData>;
export type Cell<
	TData extends RowData,
	TValue extends CellData = CellData,
> = TanStackCell<DataTableFeatures, TData, TValue>;
export type Header<
	TData extends RowData,
	TValue extends CellData = CellData,
> = TanStackHeader<DataTableFeatures, TData, TValue>;
export type Table<TData extends RowData> = TanStackTable<
	DataTableFeatures,
	TData
>;

/** A table's column list. Its accessors carry different value types, so TValue is `any` (as in v8). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableColumns<TData extends RowData> = ColumnDef<TData, any>[];

export const createColumnHelper = <TData extends RowData>() =>
	tanStackCreateColumnHelper<DataTableFeatures, TData>();

interface DataTableProps<TData extends RowData> {
	className?: string;
	columns: DataTableColumns<TData>;
	data: TData[];
	handleAction?: (
		rowIndex: number,
		action: string,
		params?: never | null,
	) => void;
	hideEmpty?: boolean;
	/** Controlled sorting state - when provided, sorting is managed externally */
	sorting?: SortingState;
	/** Callback for sorting changes - required when using controlled sorting */
	onSortingChange?: OnChangeFn<SortingState>;
	/** When true, disables internal sorting (useful when data is pre-sorted externally) */
	manualSorting?: boolean;
	/** Controlled column filters - when provided, the filters are managed externally */
	columnFilters?: ColumnFiltersState;
	/** Callback for column filter changes - required when using controlled filters */
	onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
	rowProps?: (row: Row<TData>) => React.HTMLAttributes<HTMLTableRowElement>;
}

export function DataTable<TData extends RowData>({
	columns,
	data,
	handleAction,
	className,
	hideEmpty = false,
	sorting,
	onSortingChange,
	manualSorting = false,
	columnFilters,
	onColumnFiltersChange,
	rowProps,
}: DataTableProps<TData>) {
	const table = useTable({
		features: dataTableFeatures,
		data,
		columns,
		// Support controlled sorting and column filters
		...((sorting !== undefined || columnFilters !== undefined) && {
			state: {
				...(sorting !== undefined && { sorting }),
				...(columnFilters !== undefined && { columnFilters }),
			},
		}),
		...(onSortingChange && { onSortingChange }),
		...(onColumnFiltersChange && { onColumnFiltersChange }),
		manualSorting,

		meta: {
			handleAction,
		},
	});

	return (
		<TableRoot className={className}>
			<TableHeader>
				{table.getHeaderGroups().map((headerGroup) => (
					<TableRow key={headerGroup.id}>
						{headerGroup.headers.map((header) => (
							<TableHead key={header.id}>
								{header.isPlaceholder ? null : (
									<div className="flex gap-1 items-center justify-between">
										<div className="flex gap-1 items-center">
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
											{header.column.getCanFilter() ? (
												<div>
													<Filter column={header.column} />
												</div>
											) : null}
										</div>
										{{
											asc: (
												<div
													className=" -space-y-1 cursor-pointer select-none"
													onClick={header.column.getToggleSortingHandler()}
													title={
														header.column.getCanSort()
															? header.column.getNextSortingOrder() === "asc"
																? "Sort ascending"
																: header.column.getNextSortingOrder() === "desc"
																	? "Sort descending"
																	: "Clear sort"
															: undefined
													}
												>
													<ChevronUp className="size-4" />
													<ChevronDown className="size-4 text-muted-foreground" />
												</div>
											),
											desc: (
												<div
													className=" -space-y-1 cursor-pointer select-none"
													onClick={header.column.getToggleSortingHandler()}
													title={
														header.column.getCanSort()
															? header.column.getNextSortingOrder() === "asc"
																? "Sort ascending"
																: header.column.getNextSortingOrder() === "desc"
																	? "Sort descending"
																	: "Clear sort"
															: undefined
													}
												>
													<ChevronUp className="size-4 text-muted-foreground" />
													<ChevronDown className="size-4" />
												</div>
											),
										}[header.column.getIsSorted() as string] ??
											(header.column.getCanSort() ? (
												<div
													className=" -space-y-1 cursor-pointer select-none"
													onClick={header.column.getToggleSortingHandler()}
													title={
														header.column.getCanSort()
															? header.column.getNextSortingOrder() === "asc"
																? "Sort ascending"
																: header.column.getNextSortingOrder() === "desc"
																	? "Sort descending"
																	: "Clear sort"
															: undefined
													}
												>
													<ChevronUp className="size-4 text-muted-foreground" />
													<ChevronDown className="size-4 text-muted-foreground" />
												</div>
											) : null)}
									</div>
								)}
							</TableHead>
						))}
					</TableRow>
				))}
			</TableHeader>
			<TableBody>
				{!!table.getRowModel().rows.length &&
					table.getRowModel().rows.map((row) => {
						const extraRowProps = rowProps?.(row);
						return (
							<TableRow
								key={row.id}
								data-state={row.getIsSelected() && "selected"}
								{...extraRowProps}
							>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						);
					})}
				{!table.getRowModel().rows.length && !hideEmpty && (
					<TableRow>
						<TableCell colSpan={columns.length} className="h-24 text-center">
							No results.
						</TableCell>
					</TableRow>
				)}
			</TableBody>
		</TableRoot>
	);
}

function Filter<TData extends RowData>({
	column,
}: {
	column: Column<TData, unknown>;
}) {
	const { filterVariant } = column.columnDef.meta ?? {};

	const columnFilterValue = column.getFilterValue();

	const sortedUniqueValues = React.useMemo(
		() =>
			filterVariant === "range"
				? []
				: Array.from(column.getFacetedUniqueValues().keys())
						.sort()
						.slice(0, 5000),
		[column.getFacetedUniqueValues(), filterVariant],
	);

	return filterVariant === "range" ? (
		<div>
			<div className="flex space-x-2">
				<DebouncedInput
					type="number"
					min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
					max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
					value={(columnFilterValue as [number, number] | null)?.[0] ?? ""}
					onChange={(value) =>
						column.setFilterValue((old: [number, number] | null) => [
							value,
							old?.[1],
						])
					}
					placeholder={`Min ${
						column.getFacetedMinMaxValues()?.[0] !== undefined
							? `(${column.getFacetedMinMaxValues()?.[0]})`
							: ""
					}`}
					className="w-24 border shadow rounded"
				/>
				<DebouncedInput
					type="number"
					min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
					max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
					value={(columnFilterValue as [number, number] | null)?.[1] ?? ""}
					onChange={(value) =>
						column.setFilterValue((old: [number, number] | null) => [
							old?.[0],
							value,
						])
					}
					placeholder={`Max ${
						column.getFacetedMinMaxValues()?.[1]
							? `(${column.getFacetedMinMaxValues()?.[1]})`
							: ""
					}`}
					className="w-24 border shadow rounded"
				/>
			</div>
			<div className="h-1" />
		</div>
	) : filterVariant === "select" ? (
		<Select
			value={columnFilterValue?.toString() ?? "_all"}
			onValueChange={(value) => {
				if (value === "_all") {
					return column.setFilterValue("");
				}
				return column.setFilterValue(value || "");
			}}
		>
			<SelectTrigger size="sm" className={buttonVariants({ variant: "ghost" })}>
				<SelectValue placeholder="Select" />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="_all">All</SelectItem>
				{sortedUniqueValues.map((value, index) => (
					<SelectItem value={value} key={value + index}>
						{value}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	) : (
		<>
			{/* Autocomplete suggestions from faceted values feature */}
			<datalist id={column.id + "list"}>
				{sortedUniqueValues.map((value: any, index) => (
					<option value={value} key={value + index} />
				))}
			</datalist>
			<DebouncedInput
				type="text"
				value={(columnFilterValue ?? "") as string}
				onChange={(value) => column.setFilterValue(value)}
				placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
				className="w-36 border shadow rounded"
				list={column.id + "list"}
			/>
			<div className="h-1" />
		</>
	);
}
